/**
 * PickingScannerSection - QR scanner for pick verification
 *
 * Only shown when verification mode is 'required'.
 * Handles scan, validation, and feedback with warehouse-optimized UX.
 *
 * Phase 22-02: PAP items now show PlacementOverlay after pick verification
 * for bin/shelf routing (matching catcher station behavior).
 */
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { QRScannerInput } from '@/features/catcher-station/components/QRScannerInput';
import { PlacementOverlay } from '@/features/catcher-station/components/PlacementOverlay';
import { SupersededStickerDialog } from '@/components/production/SupersededStickerDialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Scan, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useScannerAutoFocus } from '@/hooks/useScannerAutoFocus';
import { useScanFeedback } from '@/hooks/useScanFeedback';
import { parseSupersededError } from '@/lib/trpc-error-utils';
import { playErrorBeep } from '@/lib/sounds';
import { useConfirmPapPlacement } from '../hooks/usePicking';
import type { VerifyPickResult } from '../types';

/** Pending PAP placement state for PlacementOverlay */
interface PendingPlacement {
  variant: 'bin' | 'shelf';
  binLabel: string;
  garmentUnitId: string;
}

interface PickingScannerSectionProps {
  onVerify: (qrCode: string) => Promise<VerifyPickResult>;
  isLoading: boolean;
  disabled?: boolean;
  /** Location ID for PAP placement confirmation */
  locationId: string;
  /** Batch ID for cache invalidation */
  batchId: string;
}

export function PickingScannerSection({
  onVerify,
  isLoading,
  disabled,
  locationId,
  batchId,
}: PickingScannerSectionProps) {
  const { t } = useTranslation('production');
  const [lastScanned, setLastScanned] = useState<VerifyPickResult | null>(null);
  const [pendingPlacement, setPendingPlacement] = useState<PendingPlacement | null>(null);

  // SUPERSEDED sticker: the one scan block picking surfaces as a modal (needs the
  // one-tap reprint action) rather than a toast. Other errors keep the toast path.
  const feedback = useScanFeedback();

  const confirmPapPlacement = useConfirmPapPlacement(batchId);

  // USB scanners type characters as keyboard input - redirect to scanner field
  // Pause auto-focus when PlacementOverlay OR the superseded dialog is showing.
  useScannerAutoFocus({ paused: !!pendingPlacement || feedback.isModalOpen });

  const handleScan = useCallback(async (qrCode: string) => {
    try {
      const result = await onVerify(qrCode);
      setLastScanned(result);

      // Check if this is a PAP item that needs bin/shelf routing
      if (result.papPlacement && !result.alreadyPicked) {
        const placement = result.papPlacement;
        const directive = placement.binDirective;

        // Determine overlay variant
        const variant: 'bin' | 'shelf' = directive.action === 'PACKING_SHELF' ? 'shelf' : 'bin';

        setPendingPlacement({
          variant,
          binLabel: directive.binLabel || '',
          garmentUnitId: placement.garmentUnitId,
        });

        // Don't show toast yet — PlacementOverlay handles feedback
        return;
      }

      // POD items or already-picked: show toast as usual
      if (result.alreadyPicked) {
        toast.info(
          `${t('picker.alreadyPicked')} ${result.garmentInfo.sku} (${result.garmentInfo.size || 'N/A'})`,
          { icon: <Check className="h-5 w-5" /> }
        );
      } else {
        toast.success(
          `${t('picker.picked')} ${result.garmentInfo.sku} (${result.garmentInfo.size || 'N/A'})`,
          { icon: <Check className="h-5 w-5 text-green-600" /> }
        );
      }
    } catch (error) {
      // Superseded sticker → amber modal with a one-tap reprint (triggerSuperseded
      // fires its own warning buzz). All other errors keep the existing toast path.
      const superseded = parseSupersededError(error);
      if (superseded.isSuperseded) {
        feedback.triggerSuperseded(superseded.replacement);
        setLastScanned(null);
        return;
      }
      playErrorBeep();
      toast.error(error instanceof Error ? error.message : t('picker.scanFailed'));
      setLastScanned(null);
    }
  }, [onVerify, feedback, t]);

  const handlePlacementDismiss = useCallback(async () => {
    if (!pendingPlacement) return;

    try {
      const result = await confirmPapPlacement.mutateAsync({
        garmentUnitId: pendingPlacement.garmentUnitId,
        locationId,
      });

      const binLabel = result.binLabel || pendingPlacement.binLabel;
      const location = pendingPlacement.variant === 'bin'
        ? binLabel
        : t('placement.packingBench');

      setPendingPlacement(null);
      toast.success(
        t('picker.placedInBin', { location }),
        { icon: <Check className="h-5 w-5 text-green-600" /> }
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to confirm placement');
      // Keep overlay open so worker can retry
    }
  }, [pendingPlacement, confirmPapPlacement, locationId, t]);

  return (
    <>
      <Card role="region" aria-label={t('picker.scanPickedItems')}>
        {/* p-4 for touch-friendly header */}
        <CardHeader className="p-4">
          <CardTitle className="flex items-center gap-3 text-lg">
            <Scan className="h-6 w-6" aria-hidden="true" />
            {t('picker.scanPickedItems')}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <QRScannerInput
            onScan={handleScan}
            disabled={disabled || !!pendingPlacement}
            isLoading={isLoading}
          />
          {lastScanned && !lastScanned.alreadyPicked && !pendingPlacement && (
            <div
              className="mt-4 p-4 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800"
              role="status"
              aria-live="polite"
            >
              <p className="text-base font-medium text-green-800 dark:text-green-200">
                {t('picker.verified')} {lastScanned.garmentInfo.sku} ({lastScanned.garmentInfo.size || 'N/A'})
              </p>
              <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                {t('picker.order')} {lastScanned.garmentInfo.orderNumber}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* PlacementOverlay for PAP items — full-screen bin/shelf routing */}
      <PlacementOverlay
        open={!!pendingPlacement}
        variant={pendingPlacement?.variant ?? 'bin'}
        binLabel={pendingPlacement?.binLabel ?? ''}
        onDismiss={handlePlacementDismiss}
      />

      {/* Superseded-sticker dialog (amber; one-tap reprint of the live replacement) */}
      <SupersededStickerDialog
        {...feedback.supersededProps}
        locationId={locationId}
        currentStage="Picking"
      />
    </>
  );
}
